/**
 * What a receipt has to become before it is uploaded, and what the quota bar
 * says about it — the arithmetic half, with no DOM in it.
 *
 * A phone camera produces a 4032×3024 photo of a piece of paper: eight
 * megapixels and several megabytes to say "£43.20, Tesco, Tuesday". Nobody
 * ever needs that, the free plan's 512 MB would hold about a hundred of them,
 * and the upload is the slowest thing in the app on a bad connection. So the
 * browser redraws it: the long edge down to 2000px, re-encoded as WebP at
 * 0.82, plus a 400px thumbnail for the list. A phone photo lands somewhere
 * around 200–600 KB, which is roughly a tenth of what came out of the camera
 * and still legible enough to read a total off.
 *
 * The *deciding* is here — which is the half worth testing, because "does a
 * portrait photo scale by its height" and "does this file fit in what is left
 * of my quota" are questions with right answers that do not need a canvas to
 * be wrong in. `src/lib/receipt-image.ts` is the other half: the four calls
 * that actually draw the thing.
 *
 * **The server is still the enforcement.** Every ceiling below is also a
 * ceiling in `server/receipts.ts`, taken again from the plan limit resolver on
 * the request that stores the bytes, because a client can be lied to. What
 * these numbers buy is a screen that refuses in the file picker instead of
 * after a 6 MB upload.
 */

/** The long edge a stored receipt is scaled down to. */
export const LONG_EDGE = 2000

/** The long edge of the thumbnail the list draws. */
export const THUMBNAIL_EDGE = 400

/**
 * WebP quality. 0.82 is where a photograph of a printed receipt stops looking
 * like a photograph of a printed receipt and starts looking like a JPEG of
 * one; below it the small print goes first, which is the only part anybody
 * zooms in on.
 */
export const WEBP_QUALITY = 0.82

/** The thumbnail can afford to be softer — it is 400px and never read. */
export const THUMBNAIL_QUALITY = 0.7

export type Dimensions = { width: number; height: number }

/**
 * `size` scaled so its long edge is at most `maxEdge`, keeping the aspect
 * ratio, and never scaled *up* — a 900px receipt somebody has already
 * compressed is left at 900px rather than blown up to 2000 and re-encoded into
 * something bigger and no clearer.
 *
 * Both sides are rounded and floored at 1: a canvas of zero width throws, and
 * a 4000×3 scan of the edge of something is not worth crashing over.
 */
export function scaleToLongEdge(size: Dimensions, maxEdge: number): Dimensions {
  const longest = Math.max(size.width, size.height)
  if (longest <= 0) return { width: 1, height: 1 }

  const scale = Math.min(1, maxEdge / longest)
  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale)),
  }
}

/** What kind of thing was picked, or `null` for one we will not take. */
export type ReceiptKind = 'image' | 'pdf'

/**
 * The kinds this app knows how to prepare.
 *
 * HEIC is on the list because that is what an iPhone takes photos in, and
 * `createImageBitmap` decodes it on the browsers that produce it — the file
 * that goes *up* is still WebP, because the file that goes up is always
 * whatever the canvas re-encoded. SVG is deliberately absent: it is a script
 * that happens to draw a picture, and `server/receipts.ts` will not store one.
 */
export function receiptKindOf(file: { type: string; name?: string }): ReceiptKind | null {
  const type = (file.type || '').split(';')[0].trim().toLowerCase()
  if (type === 'application/pdf') return 'pdf'
  if (type === 'image/svg+xml') return null
  if (type.startsWith('image/')) return 'image'

  // Some file pickers hand over an empty type. The extension is a worse
  // question than the MIME type and is only asked when there is no MIME type.
  const extension = (file.name ?? '').toLowerCase().split('.').pop() ?? ''
  if (type === '') {
    if (extension === 'pdf') return 'pdf'
    if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'].includes(extension)) return 'image'
  }
  return null
}

/** What the API stores a file as, given what the browser produced. */
export const EXTENSIONS: Record<string, string> = {
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'application/pdf': 'pdf',
}

// ---------------------------------------------------------------------------
// The quota, before the upload
// ---------------------------------------------------------------------------

/** What `GET /api/me` says about storage. `limitBytes: null` is unlimited. */
export type Storage = {
  usedBytes: number
  limitBytes: number | null
  unlimited: boolean
  /** The largest single file, already the smaller of the plan's and the cap. */
  maxFileBytes: number
}

/**
 * The quota bar, for a given number of bytes about to be added.
 *
 * `adding` is the size of the files *after* the browser has re-encoded them,
 * not the size of what was picked — which is the point of doing the work
 * before the bar is drawn rather than after. Somebody picking a 6 MB photo
 * with 800 KB of room left should be told it fits, because by the time it is
 * sent it will be 400 KB, and telling them it does not is a bug that looks
 * like a policy.
 */
export type QuotaReading = {
  usedBytes: number
  limitBytes: number | null
  addingBytes: number
  /** Where the bar ends up: used + adding, never past the limit for drawing. */
  afterBytes: number
  /** 0..1, for the bar's width. Always 0 when there is no limit. */
  fraction: number
  /** How much over the limit this would go. 0 when it fits. */
  overBytes: number
  fits: boolean
  /** `false` when this one file is over the single-file ceiling. */
  fileFits: boolean
}

export function quotaFor(storage: Storage, addingBytes: number, largestFileBytes = 0): QuotaReading {
  const limitBytes = storage.unlimited ? null : storage.limitBytes
  const afterBytes = storage.usedBytes + addingBytes
  const overBytes = limitBytes === null ? 0 : Math.max(0, afterBytes - limitBytes)

  return {
    usedBytes: storage.usedBytes,
    limitBytes,
    addingBytes,
    afterBytes,
    fraction: limitBytes === null || limitBytes === 0 ? 0 : Math.min(1, afterBytes / limitBytes),
    overBytes,
    fits: overBytes === 0,
    fileFits: largestFileBytes <= storage.maxFileBytes,
  }
}
